import React from 'react';
import { firestoreDb } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  onSnapshot, 
  runTransaction,
  getDocs
} from 'firebase/firestore';
import { getWorkoutSets } from '../utils/badgeUtils';
import { calculateWorkoutVolume, calculateWorkoutReps, calculateWorkoutDurationDamage, MAX_SET_REPS, MAX_SET_WEIGHT, MAX_WORKOUT_VOLUME, MAX_WORKOUT_BOSS_REPS, MAX_WORKOUT_DURATION_MINUTES } from '../utils/exerciseUtils';

export const BOSS_COLLECTION = 'world_boss';
export const ACTIVE_BOSS_ID = 'current';
export const PARTICIPANTS_COLLECTION = 'world_boss_participants';

// Date d'ancrage : Lundi 12 Octobre 2026 à 00:00:00 (Semaine 1 du cycle bi-hebdomadaire)
export const ANCHOR_MONDAY = new Date(2026, 9, 12, 0, 0, 0, 0);

// Semaines exceptionnellement activées en raid (ex: prolongation du premier boss pour la semaine du 19 au 25 Octobre 2026)
export const EXCEPTIONAL_RAID_WEEKS = ['2026-10-19'];

/**
 * Retourne le lundi à 00h00:00.000 de la semaine pour une date donnée
 */
export const getMondayOfWeek = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Calcule toutes les informations relatives au cycle bi-hebdomadaire du World Boss :
 * - Semaine 1 (Semaine de Raid) :
 *     Lundi 00h00 -> Dimanche 23h59 : Combat actif (ou vaincu si terrassé avant).
 * - Semaine 2 (Semaine de Repos) :
 *     Lundi 00h00 -> Lundi 23h59 : Jour de débriefing (la bannière reste affichée tout le lundi).
 *     Mardi 00h00 -> Dimanche 23h59 : L'arène se repose, bannière masquée de l'accueil.
 * - Semaine 3 :
 *     Lundi 00h00 : Nouveau World Boss tout neuf !
 */
export const getBossCycleInfo = (now = new Date()) => {
  const currentMonday = getMondayOfWeek(now);
  const diffWeeks = Math.round((currentMonday.getTime() - ANCHOR_MONDAY.getTime()) / (7 * 24 * 60 * 60 * 1000));
  const year = currentMonday.getFullYear();
  const month = String(currentMonday.getMonth() + 1).padStart(2, '0');
  const day = String(currentMonday.getDate()).padStart(2, '0');
  const mondayKey = `${year}-${month}-${day}`;
  const isExceptionalRaid = EXCEPTIONAL_RAID_WEEKS.includes(mondayKey);

  const isRaidWeek = isExceptionalRaid || ((diffWeeks % 2 + 2) % 2 === 0);
  const cycleIndex = Math.floor(diffWeeks / 2);
  const cycleId = isExceptionalRaid ? 'dracofonte_s1' : `dracofonte_c${cycleIndex}`;

  let raidStart;
  let raidEnd;
  let debriefEnd;
  let nextBossStart;

  if (isRaidWeek) {
    raidStart = new Date(currentMonday);
    raidEnd = new Date(currentMonday);
    raidEnd.setDate(raidEnd.getDate() + 6);
    raidEnd.setHours(23, 59, 59, 999);

    debriefEnd = new Date(currentMonday);
    debriefEnd.setDate(debriefEnd.getDate() + 7);
    debriefEnd.setHours(23, 59, 59, 999);

    nextBossStart = new Date(currentMonday);
    nextBossStart.setDate(nextBossStart.getDate() + (isExceptionalRaid ? 7 : 14));
    nextBossStart.setHours(0, 0, 0, 0);
  } else {
    const prevMonday = new Date(currentMonday);
    prevMonday.setDate(prevMonday.getDate() - 7);
    raidStart = prevMonday;

    raidEnd = new Date(prevMonday);
    raidEnd.setDate(raidEnd.getDate() + 6);
    raidEnd.setHours(23, 59, 59, 999);

    debriefEnd = new Date(currentMonday);
    debriefEnd.setHours(23, 59, 59, 999);

    nextBossStart = new Date(currentMonday);
    nextBossStart.setDate(nextBossStart.getDate() + 7);
    nextBossStart.setHours(0, 0, 0, 0);
  }

  const isRaidPeriod = now >= raidStart && now <= raidEnd;
  const isDebriefDay = !isRaidPeriod && now <= debriefEnd;
  const isRestPeriod = now > raidEnd;
  const showBanner = now <= debriefEnd;

  return {
    cycleIndex,
    cycleId,
    isRaidWeek,
    isRaidPeriod,
    isDebriefDay,
    isRestPeriod,
    showBanner,
    raidStart,
    raidEnd,
    debriefEnd,
    nextBossStart
  };
};

export const canAttackBoss = (boss, now = new Date()) => {
  const cycle = getBossCycleInfo(now);
  if (!cycle.isRaidPeriod) return false;
  if (!boss || boss.status !== 'active' || (boss.currentHp || 0) <= 0) return false;
  return true;
};

export const TARGET_VOLUME_PER_ACTIVE_USER = 10000; // 10 000 kg/dégâts par athlète actif pour une semaine

/**
 * Calcule de façon synchrone les PV adaptés à partir du nombre d'athlètes actifs :
 * Formule convenue : (Nombre d'athlètes actifs) x 10 000 kg
 * Minimum de sécurité : 20 000 PV (équivalent 2 athlètes réguliers).
 */
export const computeAdaptiveHpFromActiveCount = (activeCount, targetVolumePerUser = TARGET_VOLUME_PER_ACTIVE_USER) => {
  const effectiveAthletes = Math.max(2, activeCount || 0);
  const calculatedHp = Math.round(effectiveAthletes * targetVolumePerUser);
  return Math.max(20000, Math.round(calculatedHp / 5000) * 5000);
};

/**
 * Calcule dynamiquement les PV max adaptés du World Boss en interrogeant les séances des 14 derniers jours :
 */
export const calculateAdaptiveBossHp = async (targetVolumePerUser = TARGET_VOLUME_PER_ACTIVE_USER) => {
  try {
    const fourteenDaysAgo = Date.now() - (14 * 24 * 60 * 60 * 1000);
    const workoutsSnap = await getDocs(collection(firestoreDb, "workouts"));

    const activeUserIds = new Set();
    workoutsSnap.forEach(docSnap => {
      const data = docSnap.data();
      const time = data.timestamp || (data.date ? new Date(data.date).getTime() : 0);
      if (time >= fourteenDaysAgo && data.profileId) {
        activeUserIds.add(data.profileId);
      }
    });

    return computeAdaptiveHpFromActiveCount(activeUserIds.size, targetVolumePerUser);
  } catch (err) {
    console.warn("[BossService] Erreur lors du calcul dynamique des PV :", err);
    return 40000; // Repli adapté
  }
};

/**
 * Réserve de répliques / punchlines pour les World Boss :
 * - « Pour info, balancer tout ton corps en arrière, ça ne compte pas comme une répétition. » (Dracofonte)
 * - « Ton shaker phosphorescent ne va pas soulever la barre à ta place. » (Le Barromancien)
 * - « Tu as bu trois scoops de pré-workout pour brasser autant de vent ? »
 * - « Ceinture, sangles, magnésie... tout ce cinéma pour une barre aussi légère ? »
 * - « Inutile de fixer la barre d'un air mystérieux, elle ne va pas monter toute seule. »
 * - « Tu prends 4 minutes de repos sur ton téléphone pour soulever si peu ? »
 */
export const BOSS_TAUNTS_VAULT = [
  "Pour info, balancer tout ton corps en arrière, ça ne compte pas comme une répétition.",
  "Ton shaker phosphorescent ne va pas soulever la barre à ta place.",
  "Tu as bu trois scoops de pré-workout pour brasser autant de vent ?",
  "Ceinture, sangles, magnésie... tout ce cinéma pour une barre aussi légère ?",
  "Inutile de fixer la barre d'un air mystérieux, elle ne va pas monter toute seule.",
  "Tu prends 4 minutes de repos sur ton téléphone pour soulever si peu ?"
];

export const BOSS_TEMPLATES = {
  dracofonte: {
    id: 'dracofonte',
    name: 'Dracofonte',
    title: 'Le Léviathan de Fonte',
    description: "Créature colossale forgée dans les braises des charges lourdes. Seul l'effort collectif de tous les athlètes peut en venir à bout.",
    taunt: "Pour info, balancer tout ton corps en arrière, ça ne compte pas comme une répétition.",
    ruleType: 'weight_kg',
    ruleLabel: '1 kg soulevé = 1 dégât',
    ruleDescription: "Chaque kilo soulevé lors de tes séances d'entraînement inflige 1 dégât direct au boss.",
    damageUnit: 'kg',
    baseTargetPerUser: 10000,
    minHp: 20000,
    defaultHp: 40000
  },
  barromancien: {
    id: 'barromancien',
    name: 'Le Barromancien',
    title: "L'Archimage de la Charge Pure",
    description: "Maître des arcanes et de la gravitation. Son bouclier magique dévie les charges lourdes mais se brise sous l'accumulation des répétitions.",
    taunt: "Ton shaker phosphorescent ne va pas soulever la barre à ta place.",
    ruleType: 'reps',
    ruleLabel: '1 répétition = 1 dégât',
    ruleDescription: "Chaque répétition validée lors de tes séances d'entraînement affaiblit le bouclier arcanique du magicien.",
    damageUnit: 'reps',
    baseTargetPerUser: 750,
    minHp: 1500,
    defaultHp: 3000
  },
  golem: {
    id: 'golem',
    name: 'MusculaThor',
    title: 'Le Dieu de la ~~Foudre~~ Fonte',
    description: "Colosse de pierre titanesque aux veines telluriques arcaniques. Seule une endurance acharnée dans la salle permet d'éroder sa cuirasse rocheuse.",
    taunt: "Ceinture, sangles, magnésie... tout ce cinéma pour une barre aussi légère ?",
    ruleType: 'duration_minutes',
    ruleLabel: '1h30 de séance = 1 300 dégâts',
    ruleDescription: "Chaque minute active passée sous la fonte affaiblit le Golem (90 min = 1 300 dégâts).",
    damageUnit: 'dégâts',
    baseTargetPerUser: 3500,
    minHp: 7000,
    defaultHp: 12000
  }
};

export const DEFAULT_BOSS = {
  ...BOSS_TEMPLATES.dracofonte,
  id: 'dracofonte_c0',
  cycleId: 'dracofonte_c0',
  status: 'active', // 'active' | 'defeated' | 'expired'
  rewardCoins: 150, // 150 pièces pour tous les participants
  maxHp: 40000,
  currentHp: 40000,
  totalDamage: 0,
  participantsCount: 0,
  killer: null,
  createdAt: new Date().toISOString()
};

/**
 * Synchronise et résout l'état automatique du cycle World Boss (bi-hebdomadaire)
 */
export const syncBossCycleState = async (bossData) => {
  const cycle = getBossCycleInfo();
  const bossRef = doc(firestoreDb, BOSS_COLLECTION, ACTIVE_BOSS_ID);

  try {
    // 1. Si on est dans une nouvelle semaine de raid et que le boss stocké date d'un ancien cycle
    if (cycle.isRaidWeek && bossData?.cycleId !== cycle.cycleId && bossData?.id !== cycle.cycleId) {
      const dynamicHp = await calculateAdaptiveBossHp();
      const newBoss = {
        ...DEFAULT_BOSS,
        id: cycle.cycleId,
        cycleId: cycle.cycleId,
        maxHp: dynamicHp,
        currentHp: dynamicHp,
        status: 'active',
        createdAt: cycle.raidStart.toISOString(),
        expiresAt: cycle.raidEnd.toISOString()
      };
      await setDoc(bossRef, newBoss);
      return newBoss;
    }

    // 2. Si le combat est terminé (dimanche 23h59 passé) et que le boss n'a pas été vaincu mais est resté 'active'
    if (cycle.isRestPeriod && bossData?.status === 'active') {
      const updates = {
        status: 'expired',
        expiredAt: cycle.raidEnd.toISOString(),
        updatedAt: new Date().toISOString()
      };
      await updateDoc(bossRef, updates);
      return { ...bossData, ...updates };
    }
  } catch (err) {
    console.warn("[BossService] Erreur lors de la synchronisation automatique du cycle :", err);
  }

  return bossData;
};

/**
 * Initialise le boss par défaut dans Firestore si inexistant
 */
export const initBossIfMissing = async () => {
  try {
    const bossRef = doc(firestoreDb, BOSS_COLLECTION, ACTIVE_BOSS_ID);
    const snap = await getDoc(bossRef);
    if (!snap.exists()) {
      await setDoc(bossRef, DEFAULT_BOSS);
    }
  } catch (err) {
    console.warn("Impossible d'initialiser le boss dans Firestore :", err);
  }
};

/**
 * Écoute en temps réel un boss par son identifiant
 */
export const subscribeToBossById = (bossId, callback) => {
  if (!bossId) return () => {};
  const bossRef = doc(firestoreDb, BOSS_COLLECTION, bossId);

  return onSnapshot(bossRef, (snap) => {
    const cycle = getBossCycleInfo();
    if (snap.exists()) {
      let data = { id: snap.id, ...snap.data() };
      callback({ ...data, cycleInfo: cycle });
    } else if (BOSS_TEMPLATES[bossId]) {
      callback({ ...BOSS_TEMPLATES[bossId], cycleInfo: cycle });
    } else {
      callback({ ...DEFAULT_BOSS, cycleInfo: cycle });
    }
  }, (err) => {
    console.error(`Erreur d'écoute du World Boss ${bossId} :`, err);
    callback({ ...(BOSS_TEMPLATES[bossId] || DEFAULT_BOSS), cycleInfo: getBossCycleInfo() });
  });
};

/**
 * Écoute en temps réel l'état du World Boss actif
 */
export const subscribeToActiveBoss = (callback) => {
  const bossRef = doc(firestoreDb, BOSS_COLLECTION, ACTIVE_BOSS_ID);
  
  initBossIfMissing();

  return onSnapshot(bossRef, (snap) => {
    const cycle = getBossCycleInfo();
    if (snap.exists()) {
      let data = { id: snap.id, ...snap.data() };

      // Auto-résolution si le combat de la semaine est passé
      if (cycle.isRestPeriod && data.status === 'active') {
        data.status = 'expired';
        syncBossCycleState(data);
      } else if (cycle.isRaidWeek && data.cycleId !== cycle.cycleId && data.id !== cycle.cycleId) {
        syncBossCycleState(data);
      }

      callback({ ...data, cycleInfo: cycle });
    } else {
      callback({ ...DEFAULT_BOSS, cycleInfo: cycle });
    }
  }, (err) => {
    console.error("Erreur d'écoute du World Boss actif :", err);
    callback({ ...DEFAULT_BOSS, cycleInfo: getBossCycleInfo() });
  });
};

/**
 * Écoute en temps réel les vrais meilleurs chasseurs (Top N réels de la commu)
 * Requête simple sans index composite pour une fiabilité totale
 */
export const subscribeToTopHunters = (bossId, callback, limitCount = 3) => {
  if (!bossId) return () => {};

  const q = query(
    collection(firestoreDb, PARTICIPANTS_COLLECTION),
    where("bossId", "==", bossId)
  );

  return onSnapshot(q, (snapshot) => {
    const participants = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if ((data.damage || 0) > 0) {
        participants.push({
          id: docSnap.id,
          name: data.profileName || 'Athlète',
          damage: data.damage || 0,
          profileId: data.profileId
        });
      }
    });

    // Tri en mémoire par dégâts décroissants (évite l'index composite Firestore)
    participants.sort((a, b) => b.damage - a.damage);

    const hunters = participants.slice(0, limitCount).map((p, idx) => {
      const rank = idx + 1;
      const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
      const title = rank === 1 ? 'Fléau des Titans 👑' : (rank === 2 || rank === 3) ? 'Champion du Raid ⚔️' : 'Chasseur de Raid';
      return {
        ...p,
        rank,
        medal,
        title,
        isKing: rank === 1
      };
    });

    callback(hunters);
  }, (err) => {
    console.error("Erreur d'écoute du Top Chasseurs :", err);
    callback([]);
  });
};

/**
 * Écoute la contribution et le rang réel de l'utilisateur connecté sur le boss actif
 */
export const subscribeToUserParticipation = (bossId, profileId, callback) => {
  if (!bossId || !profileId) {
    callback(null);
    return () => {};
  }

  const q = query(
    collection(firestoreDb, PARTICIPANTS_COLLECTION),
    where("bossId", "==", bossId)
  );

  return onSnapshot(q, (snapshot) => {
    const participants = [];
    let userDoc = null;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if ((data.damage || 0) > 0) {
        participants.push({ id: docSnap.id, ...data });
      }
      if (data.profileId === profileId) {
        userDoc = { id: docSnap.id, ...data };
      }
    });

    // Tri en mémoire pour obtenir le rang exact
    participants.sort((a, b) => (b.damage || 0) - (a.damage || 0));

    let userRank = null;
    if (userDoc && (userDoc.damage || 0) > 0) {
      const idx = participants.findIndex(p => p.profileId === profileId);
      userRank = idx >= 0 ? idx + 1 : null;
    }

    if (userDoc) {
      callback({
        ...userDoc,
        rank: userRank,
        totalParticipants: participants.length
      });
    } else {
      callback({
        bossId,
        profileId,
        damage: 0,
        workoutsCount: 0,
        rewardClaimed: false,
        rank: null,
        totalParticipants: participants.length
      });
    }
  }, (err) => {
    console.error("Erreur d'écoute participation utilisateur :", err);
    callback(null);
  });
};

export const MAX_WORKOUT_BOSS_DAMAGE = 50000;

/**
 * Calcule le tonnage total d'une séance d'entraînement avec gardes anti-triche/typos
 */
export const calculateWorkoutTonnage = (workout) => {
  return calculateWorkoutVolume(workout, { maxWorkout: MAX_WORKOUT_BOSS_DAMAGE });
};

/**
 * Calcule les dégâts d'une séance selon la règle du boss (poids ou répétitions)
 */
export const calculateWorkoutBossDamage = (workout, ruleType = 'weight_kg') => {
  if (ruleType === 'reps') {
    return calculateWorkoutReps(workout, { maxWorkout: MAX_WORKOUT_BOSS_REPS });
  }
  if (ruleType === 'duration_minutes') {
    return calculateWorkoutDurationDamage(workout);
  }
  return calculateWorkoutTonnage(workout);
};

/**
 * Applique les dégâts issus d'une vraie séance de musculation à un boss spécifique
 */
export const applyDamageToBoss = async (bossDocId, profile, workoutOrTonnage) => {
  if (!profile?.id) return null;
  const cycle = getBossCycleInfo();
  if (!cycle.isRaidPeriod) return { active: false, reason: 'raid_ended' };

  const bossRef = doc(firestoreDb, BOSS_COLLECTION, bossDocId);

  try {
    return await runTransaction(firestoreDb, async (transaction) => {
      const bossSnap = await transaction.get(bossRef);
      if (!bossSnap.exists()) return null;

      const bossData = bossSnap.data();
      if (bossData.status !== 'active' || (bossData.currentHp || 0) <= 0 || !cycle.isRaidPeriod) {
        return { active: false };
      }

      const ruleType = bossData.ruleType || (bossData.name === 'Le Barromancien' ? 'reps' : (bossData.ruleType === 'duration_minutes' ? 'duration_minutes' : 'weight_kg'));
      let damage = 0;
      if (ruleType === 'reps') {
        damage = Math.min(calculateWorkoutReps(workoutOrTonnage), MAX_WORKOUT_BOSS_REPS);
      } else if (ruleType === 'duration_minutes') {
        damage = calculateWorkoutDurationDamage(workoutOrTonnage);
      } else {
        damage = Math.min(calculateWorkoutTonnage(workoutOrTonnage), MAX_WORKOUT_BOSS_DAMAGE);
      }

      if (damage <= 0) return { success: false, damageDealt: 0 };

      const currentHp = bossData.currentHp || 0;
      const newHp = Math.max(0, currentHp - damage);
      const isDefeated = newHp === 0 && currentHp > 0;

      const partRef = doc(firestoreDb, PARTICIPANTS_COLLECTION, `${bossData.id}_${profile.id}`);
      const partSnap = await transaction.get(partRef);

      const isNewParticipant = !partSnap.exists() || (partSnap.data().damage || 0) === 0;
      const prevDamage = partSnap.exists() ? (partSnap.data().damage || 0) : 0;
      const prevWorkouts = partSnap.exists() ? (partSnap.data().workoutsCount || 0) : 0;

      const bossUpdates = {
        currentHp: newHp,
        totalDamage: (bossData.totalDamage || 0) + damage,
        updatedAt: new Date().toISOString()
      };

      if (isNewParticipant) {
        bossUpdates.participantsCount = (bossData.participantsCount || 0) + 1;
      }

      if (isDefeated) {
        bossUpdates.status = 'defeated';
        bossUpdates.defeatedAt = new Date().toISOString();
        bossUpdates.killer = {
          profileId: profile.id,
          name: profile.name || 'Athlète Anonyme',
          avatar: profile.avatar || null
        };
      }

      transaction.update(bossRef, bossUpdates);

      const partData = {
        bossId: bossData.id,
        profileId: profile.id,
        profileName: profile.name || 'Athlète',
        profileAvatar: profile.avatar || '',
        damage: prevDamage + damage,
        workoutsCount: prevWorkouts + 1,
        rewardClaimed: partSnap.exists() ? Boolean(partSnap.data().rewardClaimed) : false,
        lastContributedAt: new Date().toISOString()
      };

      transaction.set(partRef, partData, { merge: true });

      return {
        success: true,
        damageDealt: damage,
        damageUnit: bossData.damageUnit || (ruleType === 'reps' ? 'reps' : 'kg'),
        bossName: bossData.name,
        newHp,
        isDefeated,
        isKiller: isDefeated
      };
    });
  } catch (err) {
    console.error(`Erreur contribution boss ${bossDocId} :`, err);
    return null;
  }
};

/**
 * Applique les dégâts d'une séance terminée au boss actif, et synchronise aussi Le Barromancien
 */
export const recordWorkoutBossDamage = async (profile, workoutOrTonnage) => {
  if (!profile?.id) return null;
  const cycle = getBossCycleInfo();
  if (!cycle.isRaidPeriod) return { active: false, reason: 'raid_ended' };

  // 1. Dégâts sur le boss principal (ex: Dracofonte)
  const mainResult = await applyDamageToBoss(ACTIVE_BOSS_ID, profile, workoutOrTonnage);

  // 2. Synchronisation des dégâts sur les boss secondaires d'arène
  try {
    const barromancienRef = doc(firestoreDb, BOSS_COLLECTION, 'barromancien');
    const bSnap = await getDoc(barromancienRef);
    if (bSnap.exists() && ACTIVE_BOSS_ID !== 'barromancien') {
      await applyDamageToBoss('barromancien', profile, workoutOrTonnage);
    }
    const golemRef = doc(firestoreDb, BOSS_COLLECTION, 'golem');
    const gSnap = await getDoc(golemRef);
    if (gSnap.exists() && ACTIVE_BOSS_ID !== 'golem') {
      await applyDamageToBoss('golem', profile, workoutOrTonnage);
    }
  } catch (err) {
    console.warn("Erreur synchronisation dégâts boss secondaires :", err);
  }

  return mainResult;
};

// Queue de tapotement tactile (buffering avec synchronisation périodique garantie même en spam continu)
let tapQueue = 0;
let tapTimer = null;
let isFlushing = false;

const performFlush = async (profile, onFlush, targetBossId = ACTIVE_BOSS_ID) => {
  tapTimer = null;
  if (tapQueue <= 0 || !profile?.id) return;

  const toFlush = tapQueue;
  tapQueue = 0;
  isFlushing = true;

  try {
    const res = await recordTapDamage(profile, toFlush, targetBossId);
    if (onFlush) onFlush(res, toFlush);
  } catch (err) {
    console.error("Erreur lors de la synchronisation des taps :", err);
  } finally {
    isFlushing = false;
    // Si d'autres frappes ont été enregistrées pendant l'écriture réseau, on replanifie immédiatement
    if (tapQueue > 0 && !tapTimer) {
      tapTimer = setTimeout(() => performFlush(profile, onFlush, targetBossId), 600);
    }
  }
};

export const queueTapDamage = (profile, onFlush, targetBossId = ACTIVE_BOSS_ID) => {
  tapQueue += 1;

  // Si on a accumulé 15 frappes et qu'on n'est pas déjà en train d'écrire, on flush direct
  if (tapQueue >= 15 && !isFlushing) {
    if (tapTimer) clearTimeout(tapTimer);
    performFlush(profile, onFlush, targetBossId);
    return;
  }

  // Sinon, on s'assure qu'un timer tourne pour vider le panier dans 600ms au plus tard.
  if (!tapTimer && !isFlushing) {
    tapTimer = setTimeout(() => performFlush(profile, onFlush, targetBossId), 600);
  }
};

/**
 * Enregistre les dégâts de tapotement mini-jeu
 */
export const recordTapDamage = async (profile, tapCount = 1, targetBossId = ACTIVE_BOSS_ID) => {
  if (!profile?.id || tapCount <= 0) return null;
  const cycle = getBossCycleInfo();
  if (!cycle.isRaidPeriod) return null;

  const bossRef = doc(firestoreDb, BOSS_COLLECTION, targetBossId);

  try {
    return await runTransaction(firestoreDb, async (transaction) => {
      const bossSnap = await transaction.get(bossRef);
      if (!bossSnap.exists()) return null;

      const bossData = bossSnap.data();
      if (bossData.status !== 'active' || (bossData.currentHp || 0) <= 0 || !cycle.isRaidPeriod) return null;

      const damage = tapCount;
      const currentHp = bossData.currentHp || 0;
      const newHp = Math.max(0, currentHp - damage);
      const isDefeated = newHp === 0 && currentHp > 0;

      const partRef = doc(firestoreDb, PARTICIPANTS_COLLECTION, `${bossData.id}_${profile.id}`);
      const partSnap = await transaction.get(partRef);

      const isNewParticipant = !partSnap.exists() || (partSnap.data().damage || 0) === 0;
      const prevDamage = partSnap.exists() ? (partSnap.data().damage || 0) : 0;
      const prevTaps = partSnap.exists() ? (partSnap.data().tapsDamage || 0) : 0;

      const bossUpdates = {
        currentHp: newHp,
        totalDamage: (bossData.totalDamage || 0) + damage,
        updatedAt: new Date().toISOString()
      };

      if (isNewParticipant) {
        bossUpdates.participantsCount = (bossData.participantsCount || 0) + 1;
      }

      if (isDefeated) {
        bossUpdates.status = 'defeated';
        bossUpdates.defeatedAt = new Date().toISOString();
        bossUpdates.killer = {
          profileId: profile.id,
          name: profile.name || 'Athlète',
          avatar: profile.avatar || null
        };
      }

      transaction.update(bossRef, bossUpdates);

      transaction.set(partRef, {
        bossId: bossData.id,
        profileId: profile.id,
        profileName: profile.name || 'Athlète',
        profileAvatar: profile.avatar || '',
        damage: prevDamage + damage,
        tapsDamage: prevTaps + damage,
        workoutsCount: partSnap.exists() ? (partSnap.data().workoutsCount || 0) : 0,
        rewardClaimed: partSnap.exists() ? Boolean(partSnap.data().rewardClaimed) : false,
        lastContributedAt: new Date().toISOString()
      }, { merge: true });

      return {
        success: true,
        damageDealt: damage,
        newHp,
        isDefeated
      };
    });
  } catch (err) {
    console.error("Erreur enregistrement tap damage :", err);
    return null;
  }
};

/**
 * Permet au participant de réclamer les 150 pièces si le boss est vaincu
 */
export const claimBossReward = async (profile, targetBossId = ACTIVE_BOSS_ID) => {
  if (!profile?.id) throw new Error("Profil introuvable");
  
  const bossRef = doc(firestoreDb, BOSS_COLLECTION, targetBossId);
  const bossSnap = await getDoc(bossRef);
  if (!bossSnap.exists()) throw new Error("Boss introuvable");
  
  const bossData = bossSnap.data();
  if (bossData.status !== 'defeated') {
    throw new Error("Le boss n'a pas encore été vaincu !");
  }

  const partRef = doc(firestoreDb, PARTICIPANTS_COLLECTION, `${bossData.id}_${profile.id}`);
  const partSnap = await getDoc(partRef);
  if (!partSnap.exists() || (partSnap.data().workoutsCount || 0) < 1) {
    throw new Error("Au moins 1 séance en salle enregistrée cette semaine est requise pour réclamer le butin.");
  }

  if (partSnap.data().rewardClaimed) {
    throw new Error("Tu as déjà récupéré ton butin pour ce boss !");
  }

  const rewardCoins = bossData.rewardCoins || 150;

  // Marquer le butin comme réclamé
  await updateDoc(partRef, {
    rewardClaimed: true,
    claimedAt: new Date().toISOString()
  });

  // Ajouter les 150 pièces et incrémenter les victoires de boss
  const profileRef = doc(firestoreDb, "profiles", profile.id);
  const pSnap = await getDoc(profileRef);
  const currentCoins = pSnap.exists() ? (pSnap.data().coins || 0) : 0;
  const currentBossKills = pSnap.exists() ? (pSnap.data().bossKills || 0) : 0;

  const newBossKills = currentBossKills + 1;
  await updateDoc(profileRef, {
    coins: currentCoins + rewardCoins,
    bossKills: newBossKills
  });

  return {
    success: true,
    rewardCoins,
    bossKills: newBossKills
  };
};

/**
 * Réinitialise les PV du boss (Outil Développeur / Admin)
 */
export const resetBossHp = async (targetHp = 60000) => {
  const bossRef = doc(firestoreDb, BOSS_COLLECTION, ACTIVE_BOSS_ID);
  await updateDoc(bossRef, {
    currentHp: targetHp,
    maxHp: targetHp,
    status: 'active',
    defeatedAt: null,
    killer: null,
    updatedAt: new Date().toISOString()
  });
};

/**
 * Adapte les PV du boss actif en fonction des athlètes actifs récents
 */
export const adaptCurrentBossHp = async () => {
  try {
    const bossRef = doc(firestoreDb, BOSS_COLLECTION, ACTIVE_BOSS_ID);
    const snap = await getDoc(bossRef);
    if (!snap.exists()) return null;

    const data = snap.data();
    const dynamicHp = await calculateAdaptiveBossHp();
    const totalDamage = Number(data.totalDamage) || 0;
    const currentHp = Math.max(0, dynamicHp - totalDamage);
    const isDefeated = currentHp === 0 && totalDamage >= dynamicHp;

    const updates = {
      maxHp: dynamicHp,
      currentHp: currentHp,
      updatedAt: new Date().toISOString()
    };

    if (isDefeated && data.status !== 'defeated') {
      updates.status = 'defeated';
      if (!data.defeatedAt) updates.defeatedAt = new Date().toISOString();
    }

    await updateDoc(bossRef, updates);
    return { ...data, ...updates };
  } catch (err) {
    console.error("[BossService] Erreur adaptCurrentBossHp :", err);
    return null;
  }
};

/**
 * Détermine les titres de prestige World Boss actifs pour un profil donné.
 * Si un joueur est dans le Top 3 ET a porté le Coup de Grâce, les DEUX titres sont cumulables et affichés simultanément !
 * - Top 1 -> 👑 Fléau des Titans
 * - Top 2 et 3 -> ⚔️ Champion du Raid
 * - Coup de grâce (dernier coup) -> 🎯 Coup de Grâce
 * Les titres s'affichent directement dès que le boss est vaincu, jusqu'à l'apparition du boss suivant.
 */
export const getActiveBossTitlesForProfile = (boss, topHunters, profileId) => {
  if (!boss || boss.status !== 'defeated' || !profileId) return [];

  const titles = [];

  // 1. Titre de classement (Top 1 ou Top 2/3)
  const hunter = topHunters?.find(h => h.profileId === profileId);
  if (hunter) {
    if (hunter.rank === 1) {
      titles.push({ id: 'boss_top1', name: '👑 Fléau des Titans', isKing: true, type: 'top1' });
    } else if (hunter.rank === 2 || hunter.rank === 3) {
      titles.push({ id: `boss_top${hunter.rank}`, name: '⚔️ Champion du Raid', isKing: false, type: 'top23' });
    }
  }

  // 2. Coup de Grâce : cumulable si le joueur a également porté le coup fatal
  if (boss.killer?.profileId === profileId) {
    titles.push({ id: 'boss_last_hit', name: '🎯 Coup de Grâce', isKing: false, type: 'last_hit' });
  }

  return titles;
};

export const getActiveBossTitleForProfile = (boss, topHunters, profileId) => {
  const titles = getActiveBossTitlesForProfile(boss, topHunters, profileId);
  return titles.length > 0 ? titles[0] : null;
};

/**
 * Écoute en temps réel les titres de prestige World Boss d'un profil (tableau de titres cumulables)
 */
export const subscribeToProfileBossTitles = (profileId, callback) => {
  if (!profileId) {
    callback([]);
    return () => {};
  }

  let currentBoss = null;
  let currentHunters = [];
  let unsubHunters = null;

  const update = () => {
    const titles = getActiveBossTitlesForProfile(currentBoss, currentHunters, profileId);
    callback(titles);
  };

  const unsubBoss = subscribeToActiveBoss((b) => {
    currentBoss = b;
    if (b?.id) {
      if (unsubHunters) unsubHunters();
      unsubHunters = subscribeToTopHunters(b.id, (hunters) => {
        currentHunters = hunters;
        update();
      });
    }
    update();
  });

  return () => {
    unsubBoss();
    if (unsubHunters) unsubHunters();
  };
};

export const subscribeToProfileBossTitle = (profileId, callback) => {
  return subscribeToProfileBossTitles(profileId, (titles) => {
    callback(titles.length > 0 ? titles[0] : null);
  });
};

/**
 * Rendu sécurisé d'un titre de boss avec support du markdown strikethrough (ex: ~~Foudre~~).
 */
export const renderBossTitle = (title) => {
  if (!title) return null;
  const parts = String(title).split(/(~~.*?~~|<del>.*?<\/del>|<s>.*?<\/s>)/g);
  return parts.map((part, idx) => {
    let crossed = null;
    if (part.startsWith('~~') && part.endsWith('~~')) {
      crossed = part.slice(2, -2);
    } else if (part.startsWith('<del>') && part.endsWith('</del>')) {
      crossed = part.slice(5, -6);
    } else if (part.startsWith('<s>') && part.endsWith('</s>')) {
      crossed = part.slice(3, -4);
    }

    if (crossed !== null) {
      return React.createElement(
        'span',
        {
          key: idx,
          className: 'boss-title-strikethrough',
          style: {
            textDecoration: 'line-through',
            textDecorationColor: '#ef4444',
            textDecorationThickness: '2.5px',
            WebkitTextDecorationColor: '#ef4444',
            color: '#a1a1aa',
            opacity: 0.85,
            padding: '0 2px',
            margin: '0 4px',
            display: 'inline-block'
          }
        },
        crossed
      );
    }
    return part;
  });
};
